import React, { useMemo, useState } from 'react';
import { Avatar, Box, Tooltip, Typography, useTheme } from '@mui/material';
import { SummaryMetadata, SummaryOutput } from './AISummaryStructured';
import { useTranslation } from "react-i18next";
import { Area, AreaChart, ResponsiveContainer, Tooltip as RechartsTooltip, LabelList  } from 'recharts';
import { CategoricalChartState } from 'recharts/types/chart/types';
import ISONumber from 'components/formatting/ISONumber';
import ISODateTime from 'components/formatting/ISODateTime';

export type AISummarySparklineProps = {
  metadata: SummaryMetadata | null;
  data: SummaryOutput | null;
};

interface SparklineData {
  x: string;
  y: number;
  distribution: number;
  narrative_indices: number[] | undefined;
}

export default function AISummarySparkline(props: AISummarySparklineProps): JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();

  const { metadata, data } = props;

  if(metadata && metadata.timeline_timestamps?.length == metadata.timeline?.length) {
    // Add the max to the end of the timeline, which allows us to easily build the bucket's range as bucket to bucket+1
    metadata.timeline_timestamps?.push(metadata.max_timestamp);
  }
  
  const [sparklineTooltipIndex, setSparklineTooltipIndex] = useState<number | undefined>();

  // Render the narrative indicator
  function renderNarrativeIndicator(props: any): React.JSX.Element | null {
    if (!props.value?.length) {
      return null;
    }    
    return (
      <g><circle cx={props.x} cy={props.y} r={14} fill={theme.palette.success.main} />
      <text x={props.x} y={props.y+1} fontWeight="bold" fontSize="1.3em" fill={theme.colors.alpha.white[100]} textAnchor="middle" dominantBaseline="middle">
        {props.value?.length > 1 ? "+" : props.value?.[0] + 1}
      </text></g>
    );
  }
  
  // Generate the sparkline data for recharts
  const sparklineData = useMemo(() => {    
    if (!metadata?.timeline?.length || !metadata.timeline_timestamps?.length) {
      return;
    }
    
    const sData:SparklineData[] | undefined = metadata?.timeline.map((point: number, index: number) => {
      return {
        x: metadata?.timeline_timestamps[index],
        y: point,
        distribution: metadata?.timeline_distribution[index],
        narrative_indices: undefined
      }
    });
    
    data?.narratives?.forEach((narrative: any, index: number) => {
      if (!narrative.peak_date) {
        return;
      }
      const peakDate = new Date(narrative.peak_date);
      let nearestTimestampDistance = Infinity;
      let nearestIndex = -1;
      metadata?.timeline_timestamps?.forEach((metaTimestamp, index) => {
        if(index >= metadata?.timeline.length)
          return;

        const curDate = new Date(metaTimestamp);
        const timestampDistance = Math.abs(curDate - peakDate);
        if (timestampDistance < nearestTimestampDistance) {
          nearestTimestampDistance = timestampDistance;
          nearestIndex = index;
        }
      });
      if (sData && nearestIndex >= 0) {
        sData[nearestIndex].narrative_indices = sData[nearestIndex].narrative_indices || [];
        sData[nearestIndex]?.narrative_indices?.push(index);
      }
    });
    return sData;
  }, [props.metadata, props.data?.narratives])
  
  let timeFormat = { year: 'numeric', month: 'long', day: 'numeric' , hour: 'numeric', minute: 'numeric'};
  if(metadata?.min_timestamp && metadata?.max_timestamp) {
    const minT = new Date(metadata.max_timestamp);
    const maxT = new Date(metadata.min_timestamp);
    const showTimesOnDates = (maxT - minT) >= 7*86400*1000;

    if(minT.getFullYear() === maxT.getFullYear() && minT.getFullYear() === (new Date()).getFullYear()) {
      // Just this year
      if(showTimesOnDates) {
        timeFormat = { month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric'};
      } else {
        timeFormat = { month: 'short', day: 'numeric' };
      }
    } else {
      // Spans multiple years, or not this year
      if(showTimesOnDates) {
        timeFormat = { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric'};
      } else {
        timeFormat = { year: 'numeric', month: 'short', day: 'numeric' };
      }
    }
  }

  return (
    <>{!!sparklineData?.length &&
      <>
          <Tooltip
            open={sparklineTooltipIndex !== undefined}
            followCursor={true}
            title={
            <>{sparklineTooltipIndex !== undefined &&
              <Box sx={{minWidth:"150px"}}>
              <ISODateTime 
                typographyVariant="h4"
                sx={{color: "inherit", pb:1}}
                isoString={sparklineData[sparklineTooltipIndex]?.x} format={timeFormat} />
                &nbsp;-&nbsp;
              <ISODateTime 
                typographyVariant="h4"
                sx={{color: "inherit", pb:1}}
                isoString={metadata?.timeline_timestamps[sparklineTooltipIndex+1] || sparklineData[sparklineTooltipIndex]?.x} format={timeFormat} /> 
              <Box>
                <Typography variant="h4" sx={{display: "inline-block", fontWeight:"normal"}}>
                  {i18n.t("records")}:
                </Typography>
                <Box sx={{display: "inline-block", pl:1}}>
                  <ISONumber 
                    typographyVariant="h4"
                    sx={{color: theme.palette.primary.main, display: "inline-block"}}
                    number={sparklineData[sparklineTooltipIndex]?.y} />
                </Box>
              </Box>
              {sparklineData[sparklineTooltipIndex]?.narrative_indices?.length > 0 &&
              <>
                <Typography variant="h4" sx={{pt:1, fontWeight:"normal", pb:1}}> {i18n.t("widget-types.ai-summary-structured.narrative-peaks")}</Typography>
                {sparklineData[sparklineTooltipIndex]?.narrative_indices?.map((ni:number) => {
                  return (
                    <Box key={ni} sx={{display: "flex", pr:1, pb:1, alignItems: "center"}}>
                      <Box sx={{pr: 1}}>
                        <Avatar sx={{color:theme.colors.alpha.white, backgroundColor: theme.colors.secondary.main}}>
                          {ni+1}
                        </Avatar>
                      </Box>
                      {props.data?.narratives && props.data?.narratives[ni]?.title || ""}
                    </Box>
                  );
                })}
              </>
              }
            </Box>
            }</>
          }>
          <Box sx={{ height: "150px" }}>
            <ResponsiveContainer>
              <AreaChart 
                style={{ cursor: "pointer" }}
                margin={{ top: 12, right: 18, bottom: 12, left: 18 }}
                onMouseMove={(nextState: CategoricalChartState) => setSparklineTooltipIndex(nextState.activeTooltipIndex)}
                onMouseLeave={() => setSparklineTooltipIndex(undefined)}
                data={sparklineData}
              >
              <Area
                type="monotone"
                dot={{
                  stroke: theme.palette.primary.main,
                  fill: theme.colors.primary.lighter,
                  strokeWidth: 2, r: 5, opacity: 1 }}
                dataKey="y"
                stroke= {theme.palette.primary.main}
                strokeWidth="2"
                fill={theme.colors.primary.lighter} >
                <LabelList
                  dataKey={`narrative_indices`}
                  content={renderNarrativeIndicator} />
                </Area>
                <RechartsTooltip content={() => null} />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        </Tooltip>
      </>
    }</>
  );
}